import React, { useRef, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight, Activity } from 'lucide-react';
import Button from '../components/Button';
import CareerCard from '../components/CareerCard';
import SEO from '../components/SEO';
import { getAllCareers, getCategories } from '../services/careerService';
import AICareerScout from '../components/AICareerScout';
import NewsletterForm from '../components/NewsletterForm';
import { track } from '../services/track';

const HomePage = () => {
  const [careers, setCareers] = useState<any[]>([]);
  const [releaseStatus, setReleaseStatus] = useState<any>(null);
  const categories = getCategories();
  const sliderRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const start = performance.now();
    
    // Load Careers
    getAllCareers().then(data => {
      setCareers(data);
      console.debug(`[Perf] HomePage Careers Loaded: ${Math.round(performance.now() - start)}ms`);
    });

    // Load Release Status
    fetch('/data/release.json')
      .then(res => res.json())
      .then(setReleaseStatus)
      .catch(() => console.warn('Release status file not found'));

    // Track home view
    track('home_view');
  }, []);

  const scrollSlider = (direction: 'left' | 'right') => {
    if (sliderRef.current) {
      const scrollAmount = sliderRef.current.clientWidth * 0.8;
      const newScrollLeft = direction === 'left' 
        ? sliderRef.current.scrollLeft - scrollAmount 
        : sliderRef.current.scrollLeft + scrollAmount;
      
      sliderRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="w-full">
      <SEO 
        title="Discover Your Future Path"
        description="FuturePath Teens helps teenagers discover their future paths through video-first career content. Explore careers in STEM, Creative, Health, and more."
      />
      
      <section className="relative overflow-hidden bg-brand-bg py-20 lg:py-32 border-b border-brand-border">
        <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-brand-cyan/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-80 h-80 bg-brand-green/5 rounded-full blur-3xl" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-brand-dark border border-brand-border text-brand-cyan text-sm font-medium mb-6">
            DISCOVER CAREERS
          </span>
          <h1 className="text-5xl md:text-7xl font-heading font-bold text-brand-white mb-6 tracking-tight leading-tight">
            FuturePath <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-cyan to-brand-green">Teens</span>
          </h1>
          <p className="max-w-2xl mx-auto text-lg md:text-xl text-brand-grey2 mb-10 leading-relaxed">
            Discover your future career — one simple guide at a time.
            Explore real-world careers explained in a way teens can understand.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/careers">
              <Button size="lg" className="w-full sm:w-auto shadow-lg shadow-brand-cyan/20">Start Exploring</Button>
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 bg-brand-dark">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-8">
            <div>
              <h2 className="text-3xl font-heading font-semibold text-brand-white mb-2">Explore Future Paths</h2>
              <p className="text-brand-grey2">Featured and trending careers for you.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => scrollSlider('left')} className="p-2 rounded-full border border-brand-border text-brand-grey1 hover:text-brand-cyan hover:border-brand-cyan transition-colors">
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button onClick={() => scrollSlider('right')} className="p-2 rounded-full border border-brand-border text-brand-grey1 hover:text-brand-cyan hover:border-brand-cyan transition-colors">
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div ref={sliderRef} className="flex gap-6 overflow-x-auto pb-8 snap-x snap-mandatory scrollbar-hide" style={{ scrollbarWidth: 'none' }}>
            {careers.map((career, idx) => (
              <div key={career.slug} className="min-w-[300px] md:min-w-[350px] snap-start">
                <CareerCard career={career} priority={idx === 0 ? 'high' : 'auto'} />
              </div>
            ))}
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <NewsletterForm />
      </div>

      <AICareerScout />

      <section className="py-20 bg-brand-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-heading font-semibold text-brand-white mb-12 text-center">Browse by Category</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((cat) => (
              <Link 
                key={cat.id} 
                to={`/categories/${cat.id}`} 
                onClick={() => track('category_filter', { id: cat.id })}
                className="group p-8 rounded-2xl bg-brand-light border border-brand-border hover:border-brand-cyan transition-all duration-300 hover:-translate-y-1"
              >
                <h3 className="text-xl font-heading font-semibold text-brand-white mb-2 group-hover:text-brand-cyan transition-colors">{cat.label}</h3>
                <p className="text-brand-grey2 text-sm mb-4">{cat.description}</p>
                <div className="flex items-center text-brand-cyan text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity -translate-x-2 group-hover:translate-x-0">
                  View Careers <ArrowRight className="w-4 h-4 ml-2" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Release Status Footer Block */}
      {releaseStatus && (
        <section className="py-12 bg-brand-dark/50 border-t border-brand-border">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6 bg-brand-bg/40 p-6 rounded-2xl border border-brand-border">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-full bg-brand-cyan/10 text-brand-cyan">
                  <Activity className="w-6 h-6" />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-widest text-brand-grey2 font-bold mb-1">Release Status</p>
                  <h4 className="text-brand-white font-heading font-semibold">Update: {releaseStatus.week}</h4>
                </div>
              </div>
              <div className="flex flex-wrap justify-center gap-8">
                <div className="text-center">
                  <p className="text-2xl font-bold text-brand-white">{releaseStatus.careersAdded}</p>
                  <p className="text-xs text-brand-grey2">Careers Online</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-brand-cyan">{releaseStatus.shortsLive}/{releaseStatus.target}</p>
                  <p className="text-xs text-brand-grey2">Target Shorts</p>
                </div>
                <Link to="/shorts" className="flex items-center">
                  <Button size="sm" variant="secondary" className="text-xs">
                    View Live Feed
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default HomePage;
